import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { createSkiTransfer } from '../api';

export default function TransferOwnershipDialog({ open, onClose, ski, onSuccess }) {
  const [toUserEmail, setToUserEmail] = useState('');
  const [includeTests, setIncludeTests] = useState(false);
  const [includeRaces, setIncludeRaces] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!toUserEmail || !toUserEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    try {
      await createSkiTransfer(ski.id, toUserEmail, includeTests, includeRaces);
      setToUserEmail('');
      setIncludeTests(false);
      setIncludeRaces(false);
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create transfer request.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setToUserEmail('');
      setIncludeTests(false);
      setIncludeRaces(false);
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Transfer Ski Ownership</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <Alert severity="info">
            You are transferring ownership of <strong>{ski?.brand} Ski #{ski?.ski_number}</strong> to another user.
            Once they accept, the ski will be removed from your fleet and added to theirs.
          </Alert>

          <TextField
            label="Recipient Email"
            type="email"
            value={toUserEmail}
            onChange={(e) => setToUserEmail(e.target.value)}
            required
            fullWidth
            placeholder="recipient@example.com"
            disabled={loading}
            helperText="Enter the email address of the person you're transferring the ski to"
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Include Additional Data (Optional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You can choose to include test and race data associated with this ski. 
              Test data will include weather conditions, location, and your ski's rank, but not other skis tested against.
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeTests}
                  onChange={(e) => setIncludeTests(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Include test data (weather, location, rank)"
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeRaces}
                  onChange={(e) => setIncludeRaces(e.target.checked)}
                  disabled={loading}
                />
              }
              label="Include race data"
            />
          </Box>

          {error && (
            <Alert severity="error">{error}</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !toUserEmail}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Sending...' : 'Send Transfer Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

